import { useCallback, useEffect, useState } from 'react'
import {
  getCloudStorageFiles,
  getCloudStorageSummary,
  type CloudStorageFilesParams,
  type CloudStorageFilesResult,
  type CloudStorageSummary,
} from '@services/api/cloudStorage/cloud-storage.api'
import type {
  CloudStorageFile,
  CloudStoragePlan,
  StorageCategory,
  StorageClinicUsage,
} from '@components/pages/Settings/System/CloudStorage/cloudStorageSettings.constants'

export interface UseCloudStorageResult {
  /** Plan card + segmented usage bar. */
  summaryLoading: boolean
  summaryError: string | null
  plan: CloudStoragePlan | null
  categories: StorageCategory[]
  /** Usage for the active clinic. */
  clinics: StorageClinicUsage[]
  reloadSummary: () => void

  /** Server-side searched/filtered/paged file list. */
  filesLoading: boolean
  filesError: string | null
  files: CloudStorageFile[]
  total: number

  /**
   * Re-fetches both the summary and the file list. Uploads happen in other
   * modules (Files, X-ray viewer, …), so this report has no way to know its
   * numbers went stale — the page offers a manual refresh instead.
   */
  reload: () => void
}

type ClinicLoadState<T> = {
  clinicId?: string
  data: T | null
  loading: boolean
  error: string | null
}

/** Loads the selected scope and hides results from a previous clinic selection. */
export function useCloudStorage(
  filesParams: Omit<CloudStorageFilesParams, 'clinicId'> & {
    clinicId?: string
  },
): UseCloudStorageResult {
  const [summaryState, setSummaryState] = useState<
    ClinicLoadState<CloudStorageSummary>
  >({
    data: null,
    loading: false,
    error: null,
  })
  const [summaryReloadToken, setSummaryReloadToken] = useState(0)
  const [filesState, setFilesState] = useState<
    ClinicLoadState<CloudStorageFilesResult>
  >({
    data: null,
    loading: false,
    error: null,
  })
  const [filesReloadToken, setFilesReloadToken] = useState(0)

  const reloadSummary = useCallback(
    () => setSummaryReloadToken((t) => t + 1),
    [],
  )

  const reload = useCallback(() => {
    setSummaryReloadToken((t) => t + 1)
    setFilesReloadToken((t) => t + 1)
  }, [])

  const { search, category, type, clinicId, page, pageSize } = filesParams
  const enabled = Boolean(clinicId)

  useEffect(() => {
    if (!clinicId) return
    let cancelled = false
    setSummaryState((previous) => ({
      clinicId,
      data: previous.clinicId === clinicId ? previous.data : null,
      loading: true,
      error: null,
    }))

    getCloudStorageSummary({ clinicId })
      .then((summary) => {
        if (cancelled) return
        setSummaryState({
          clinicId,
          data: summary,
          loading: false,
          error: null,
        })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setSummaryState((previous) => ({
          ...previous,
          loading: false,
          error:
            e instanceof Error ? e.message : 'Failed to load storage summary',
        }))
      })

    return () => {
      cancelled = true
    }
  }, [summaryReloadToken, clinicId])

  useEffect(() => {
    if (!clinicId) return
    let cancelled = false
    setFilesState((previous) => ({
      clinicId,
      data: previous.clinicId === clinicId ? previous.data : null,
      loading: true,
      error: null,
    }))

    getCloudStorageFiles({ search, category, type, clinicId, page, pageSize })
      .then((result) => {
        if (cancelled) return
        setFilesState({ clinicId, data: result, loading: false, error: null })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setFilesState((previous) => ({
          ...previous,
          loading: false,
          error:
            e instanceof Error ? e.message : 'Failed to load storage files',
        }))
      })

    return () => {
      cancelled = true
    }
  }, [search, category, type, clinicId, page, pageSize, filesReloadToken])

  // Hide old data during the render that changes clinics, before effects run.
  const activeSummary =
    enabled && summaryState.clinicId === clinicId ? summaryState : undefined
  const activeFiles =
    enabled && filesState.clinicId === clinicId ? filesState : undefined

  return {
    summaryLoading: enabled && (!activeSummary || activeSummary.loading),
    summaryError: activeSummary?.error ?? null,
    plan: activeSummary?.data?.plan ?? null,
    categories: activeSummary?.data?.categories ?? [],
    clinics: activeSummary?.data?.clinics ?? [],
    reloadSummary,
    filesLoading: enabled && (!activeFiles || activeFiles.loading),
    filesError: activeFiles?.error ?? null,
    files: activeFiles?.data?.items ?? [],
    total: activeFiles?.data?.total ?? 0,
    reload,
  }
}
